import {
  type JudgeEvent,
  type JudgeRequest,
  parseEventStream,
} from "@fab-judge/schema";
import { ApiError, authedFetch, readApiError } from "./client";
import { mockJudgeResponse } from "./mock-stream";

/**
 * The single seam between the UI and the judge backend.
 *
 * Streams NDJSON from the `judge` edge function. Set `VITE_USE_MOCK=1` to
 * stream the in-browser mock instead (UI work without a backend).
 */
const USE_MOCK = import.meta.env.VITE_USE_MOCK === "1";

export async function* streamJudge(
  request: JudgeRequest,
  signal?: AbortSignal,
): AsyncGenerator<JudgeEvent> {
  if (USE_MOCK) {
    // A magic phrase lets the daily-limit dialog be exercised without a backend.
    const last = request.messages.filter((m) => m.role === "user").at(-1);
    if (last && /nolimit/i.test(last.content)) {
      throw new ApiError(
        429,
        "daily_limit_reached",
        "You have reached the daily question limit.",
      );
    }
    yield* parseEventStream(mockJudgeResponse(request, signal));
    return;
  }

  const response = await authedFetch("/judge", {
    method: "POST",
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    throw await readApiError(response);
  }

  yield* parseEventStream(response.body);
}

/** Send a thumbs up/down for a completed answer. Fire-and-forget from the UI. */
export async function sendFeedback(
  conversationId: string,
  rating: "up" | "down",
): Promise<void> {
  if (USE_MOCK) {
    console.info("[mock] feedback", { conversationId, rating });
    return;
  }

  try {
    const response = await authedFetch("/feedback", {
      method: "POST",
      body: JSON.stringify({ conversationId, rating }),
    });
    if (!response.ok) console.warn("feedback failed", response.status);
  } catch (error) {
    // Rating is best-effort; ignore network errors.
    console.warn("feedback failed", error);
  }
}
